import { getResourceInfo } from '@/api';
import { ExtendFileInfo } from '@/stores/cvc';
// MAX_LISTED_FAILURES and the "and N more" arithmetic moved to @/util/failureList
// when the upload-slot request became a second batch surface needing the same
// cap; the reasoning behind the number travelled with it.
import { listFailedNames } from '@/util/failureList';
import { formatSToFullTime } from '@/util/formatter';
import { getUrlExpiry } from '@/util/url';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import useConfirmModal from './useConfirmModal';

const useDownload = <E extends ExtendFileInfo & { resource_id?: string }>() => {
  const { openConfirmModal } = useConfirmModal();
  const { t } = useTranslation();
  const onDownload = useCallback(
    async (downloadList: E[]) => {
      const list = downloadList.filter((file) => !file.isFetching);
      if (!list.length) {
        return;
      }
      const items = await Promise.all(
        list.map(async (item) => {
          /**
           * @workaround 다운로드시 expired된 파일을 업데이트할떄, resource_id가 필요함.
           * result의 경우, resource_id와 id가 일치하지 않음(processing되서 나오는 파일과 아이템 생성시 아이디가 다르기 때문)
           * resource_id가 없는 경우, id를 사용하도록 수정.
           */
          const {
            data: { data },
          } = await getResourceInfo(item.resource_id || item.id);
          return {
            ...item,
            originalUrl: data.original.url,
            transcodedUrl: data.transcoded[0].url,
            duration: data.original.metadata.props.duration,
            // Absolute expiry (ms) normalised across signing schemes, or null
            // when the URL carries no recognisable expiry parameter (ISSUE-027).
            originalExpires: getUrlExpiry(data.original.url),
          };
        })
      );
      const downloadItems = list.map((item) => {
        const target = items.find((i) => i.id === item.id);
        return target || item;
      });
      const downloadNumberMap: Record<string, number> = {};
      // Collected, not announced: the loop continues past a failure, so the
      // names are gathered here and raised ONCE after the run (ISSUE-056).
      const failedNames: string[] = [];
      const downloadFilesSequentially = async () => {
        for (const file of downloadItems) {
          let response = await fetch(file.originalUrl as string);
          if (response.status === 403) {
            // Signed URL rejected (signature expired). Refresh the resource
            // exactly once and retry; only a second failure surfaces an error
            // to the user (ISSUE-027).
            const {
              data: { data },
            } = await getResourceInfo(file.resource_id || file.id);
            file.originalUrl = data.original.url;
            file.transcodedUrl = data.transcoded[0].url;
            file.originalExpires = getUrlExpiry(data.original.url);
            response = await fetch(file.originalUrl as string);
          }
          if (!response.ok) {
            // Skip this file and continue with the remaining downloads; the
            // whole run's failures surface together once the loop is done.
            failedNames.push(file.name);
            continue;
          }
          const blob = await response.blob();
          let { name } = file;
          const exec = file.name.match(/\.[0-9a-z]+$/i)?.[0];
          if (downloadNumberMap[file.name]) {
            name = `${name.replace(/\.[^/.]+$/, '')} ${formatSToFullTime(
              Date.now()
            )}(${downloadNumberMap[file.name]})${exec}`;
            downloadNumberMap[file.name] = downloadNumberMap[file.name]! + 1;
          } else {
            name = `${name.replace(/\.[^/.]+$/, '')} ${formatSToFullTime(
              Date.now()
            )}${exec}`;
            downloadNumberMap[file.name] = 1;
          }
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = name;
          a.click();
          a.remove();

          window.URL.revokeObjectURL(url);
          await new Promise((resolve) => setTimeout(resolve, 200)); // 200ms interval
        }
      };
      await downloadFilesSequentially();
      if (failedNames.length) {
        // ONE acknowledgement for the whole run, through the app's confirm-modal
        // channel (the ISSUE-009 precedent) — never one modal per failed file.
        const { names, more } = listFailedNames(failedNames);
        openConfirmModal({
          message: more
            ? t('Download failed: {{names}} and {{more}} more', {
                names,
                more,
              })
            : t('Download failed: {{names}}', { names }),
          confirmText: t('OK') as string,
          hideCancel: true,
        });
      }
      return items;
    },
    [openConfirmModal, t]
  );
  return {
    onDownload,
  };
};
export default useDownload;
